import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ChevronDown, Search, Info, ArrowUpDown } from 'lucide-react';
import { useScreenSize } from '@/hooks';
import { useToast } from '@/hooks/useToast';
import Button from '@/components/common/Button/Button';
import coinsData from '@/utils/coins';
import socket from '@/utils/socketInstance';
import { 
  C, encode, decode, forceSatoshiFormat, 
  storage, Event, __, wait, getUID 
} from '@/utils';
import {
  getSwapEstimate,
  performSwap
} from '@/services/swap/swapService';
import styles from './SwapTab.module.css';

/**
 * SwapTab Component
 * Currency swap functionality with modern UI
 * Uses React Query for API calls and socket for real-time updates
 */
const SwapTab = () => {
  const { isMobile } = useScreenSize();
  const { showSuccess, showError } = useToast();
  
  // State
  const [fromCoin, setFromCoin] = useState(storage.getKey('fromCoin') || 'BTC');
  const [toCoin, setToCoin] = useState(storage.getKey('toCoin') || 'ETH');
  const [amount, setAmount] = useState('');
  const [toAmount, setToAmount] = useState('0');
  const [baseRate, setBaseRate] = useState('0');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFromDropdownOpen, setIsFromDropdownOpen] = useState(false);
  const [isToDropdownOpen, setIsToDropdownOpen] = useState(false);
  const [credits, setCredits] = useState({});
  const [activeCoins, setActiveCoins] = useState(
    localStorage.getItem('active_coins')?.split(',').map(String) || ['1', '2', '3', '4']
  );

  // Filter coins
  const filteredCoins = coinsData
    .filter(coin => {
      if (!activeCoins.includes(coin.id.toString())) return false;
      if (coin.preffix === 'BJT' || coin.preffix === 'SC') return false;
      return coin.preffix.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      if (a.preffix === 'BTC') return -1;
      if (b.preffix === 'BTC') return 1;
      return 0;
    });

  const fromCoinData = coinsData.find(coin => coin.preffix === fromCoin);
  const toCoinData = coinsData.find(coin => coin.preffix === toCoin);

  // Fetch swap estimate
  const { data: estimateData, isLoading: isLoadingEstimate } = useQuery({
    queryKey: ['swapEstimate', fromCoin, toCoin, amount],
    queryFn: () => {
      const amountToFetch = amount && parseFloat(amount) > 0 ? amount : '1';
      return getSwapEstimate(fromCoin, toCoin, amountToFetch);
    },
    enabled: !!fromCoin && !!toCoin && fromCoin !== toCoin,
    staleTime: 30000, // 30 seconds
    retry: 2,
  });

  // Update amounts when estimate changes
  useEffect(() => {
    if (estimateData?.success) {
      // Always fetch base rate (for 1 unit)
      if (amount && parseFloat(amount) > 0) {
        const inputAmount = parseFloat(amount);
        const feePercentage = fromCoin.toLowerCase() === 'inr' ? 0.15 :
                             fromCoin.toLowerCase() === 'bjt' ? 0 : 0.01;
        const amountAfterFee = inputAmount * (1 - feePercentage);
        const estimatedAmount = amountAfterFee * (estimateData.data.estimatedAmount / inputAmount);
        setToAmount(estimatedAmount.toFixed(8));
      } else {
        setToAmount('0');
      }
      
      // Set base rate (rate for 1 unit)
      if (estimateData.data.exchangeRates) {
        const rate = estimateData.data.estimatedAmount / (amount && parseFloat(amount) > 0 ? parseFloat(amount) : 1);
        setBaseRate(rate.toFixed(8));
      }
    }
  }, [estimateData, amount, fromCoin, toCoin]);

  // Socket: Listen for credit updates
  useEffect(() => {
    const token = storage.getKey('token');
    if (!token || !socket) return;

    socket.emit(C.CREDIT, encode({ token, coin: fromCoin }));

    const handleCredit = (data) => {
      const decoded = decode(data);
      if (decoded?.credit) {
        setCredits(decoded.credit);
        storage.setKey('credits', decoded.credit);
      }
    };

    const handleCreditUpdate = (data) => {
      const decoded = decode(data);
      if (decoded?.value && decoded?.coin) {
        setCredits(prev => ({
          ...prev,
          [__.lowerCase(decoded.coin)]: decoded.value
        }));
      }
    };

    socket.on(C.CREDIT, handleCredit);
    socket.on(C.UPDATE_CREDIT, handleCreditUpdate);

    return () => {
      socket.off(C.CREDIT, handleCredit);
      socket.off(C.UPDATE_CREDIT, handleCreditUpdate);
    };
  }, [fromCoin]);

  // Listen for coin_changed events
  useEffect(() => {
    const handleFromCoinChanged = (coinName) => {
      setFromCoin(coinName);
    };

    const handleToCoinChanged = (coinName) => {
      setToCoin(coinName);
    };

    Event.on('from_coin_changed', handleFromCoinChanged);
    Event.on('to_coin_changed', handleToCoinChanged);

    return () => {
      Event.off('from_coin_changed', handleFromCoinChanged);
      Event.off('to_coin_changed', handleToCoinChanged);
    };
  }, []);

  // Swap mutation
  const swapMutation = useMutation({
    mutationFn: performSwap,
    onSuccess: (data) => {
      if (data.success) {
        showSuccess('Swap completed successfully');
        setAmount('');
        setToAmount('0');
        // Refresh credits
        const token = storage.getKey('token');
        if (token && socket) {
          socket.emit(C.CREDIT, encode({ token, coin: fromCoin }));
          socket.emit(C.CREDIT, encode({ token, coin: toCoin }));
        }
        setTimeout(() => window.location.reload(), 2000);
      } else {
        showError(data.message || 'Swap failed');
      }
    },
    onError: (error) => {
      showError(error.message || 'Swap failed');
    }
  });

  // Handle coin selection
  const handleFromCoinSelect = (coinName) => {
    if (coinName === toCoin) {
      // Swap coins if selecting same as target
      setToCoin(fromCoin);
      storage.setKey('toCoin', fromCoin);
    }
    storage.setKey('fromCoin', coinName);
    setFromCoin(coinName);
    setIsFromDropdownOpen(false);
    wait(200).then(() => {
      Event.emit('from_coin_changed', coinName);
    });
  };

  const handleToCoinSelect = (coinName) => {
    if (coinName === fromCoin) {
      // Swap coins if selecting same as source
      setFromCoin(toCoin);
      storage.setKey('fromCoin', toCoin);
    }
    storage.setKey('toCoin', coinName);
    setToCoin(coinName);
    setIsToDropdownOpen(false);
    wait(200).then(() => {
      Event.emit('to_coin_changed', coinName);
    });
  };

  // Handle swap button (swap coins)
  const handleSwapCoins = () => {
    const tempFrom = fromCoin;
    const tempFromAmount = amount;
    
    setFromCoin(toCoin);
    setToCoin(tempFrom);
    setAmount(toAmount);
    setToAmount(tempFromAmount);
    
    storage.setKey('fromCoin', toCoin);
    storage.setKey('toCoin', tempFrom);
    
    Event.emit('from_coin_changed', toCoin);
    Event.emit('to_coin_changed', tempFrom);
  };

  // Handle amount change
  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  // Handle swap now
  const handleSwapNow = () => {
    if (!amount || parseFloat(amount) <= 0) {
      showError('Please enter a valid amount');
      return;
    }

    if (fromCoin === toCoin) {
      showError('Cannot swap to the same currency');
      return;
    }

    swapMutation.mutate({
      fromCurrency: fromCoin,
      toCurrency: toCoin,
      amount: parseFloat(amount)
    });
  };

  // Calculate fee
  const calculateFee = () => {
    if (fromCoin.toLowerCase() === 'bjt') return 0;
    if (!amount || parseFloat(amount) <= 0) return 0;
    const amountNum = parseFloat(amount);
    const feePercentage = fromCoin.toLowerCase() === 'inr' ? 0.15 : 0.01;
    return amountNum * feePercentage;
  };

  // Get fee text
  const getFeeText = () => {
    if (fromCoin.toLowerCase() === 'bjt') return 'No Fee';
    const fee = calculateFee();
    const feePercentage = fromCoin.toLowerCase() === 'inr' ? '15%' : '1%';
    return `${feePercentage} (${fee.toFixed(8)} ${fromCoin})`;
  };

  // Helper function to get coin image path
  const getCoinImagePath = (coin) => {
    if (!coin || !coin.image) return '/assets/images/coins/BTC.webp';
    const coinImageMap = {
      'USDT': 'USDT.webp',
      'TRX': 'TRON.webp',
    };
    let imageName = coinImageMap[coin.preffix] || coin.image;
    imageName = imageName.replace(/^coins\//, '').replace(/^assets\/images\//, '');
    return `/assets/images/coins/${imageName}`;
  };

  // Render dropdown
  const renderDropdown = (isOpen, coinData, onSelect, type) => {
    if (!isOpen) return null;
    
    return (
      <div className={styles.dropdownPanel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.searchContainer}>
          <Search size={16} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search coins..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
        </div>

        <div className={styles.coinList}>
          {filteredCoins.map((coin) => (
            <div
              key={coin.id}
              className={`${styles.coinOption} ${coinData?.preffix === coin.preffix ? styles.coinOptionSelected : ''}`}
              onClick={() => onSelect(coin.preffix)}
            >
              <img
                src={getCoinImagePath(coin)}
                alt={coin.preffix}
                className={styles.coinImage}
                onError={(e) => {
                  if (coin.preffix === 'USDT' && e.target.src.includes('USDT.webp')) {
                    e.target.src = '/assets/images/coins/TETHER.webp';
                  } else {
                    e.target.src = '/assets/images/coins/BTC.webp';
                  }
                }}
              />
              <span className={styles.coinName}>{coin.preffix}</span>
              <span className={styles.coinBalance}>
                {forceSatoshiFormat(credits[__.lowerCase(coin.preffix)] || 0)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.dropdownContainer}`)) {
        setIsFromDropdownOpen(false);
        setIsToDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const isLoading = swapMutation.isPending || isLoadingEstimate;

  return (
    <div className={styles.swapTab}>
      {/* From Section */}
      <div className={styles.formGroup}>
        <label className={styles.label}>From</label>
        <div className={styles.inputGroup}>
          <input
            type="text"
            placeholder="0.00"
            value={amount}
            onChange={handleAmountChange}
            className={styles.amountInput}
          />
          <div className={`${styles.dropdownContainer} ${styles.dropdownContainer}`}>
            <button
              className={styles.dropdownButton}
              onClick={(e) => {
                e.stopPropagation();
                setIsFromDropdownOpen(!isFromDropdownOpen);
                setIsToDropdownOpen(false);
              }}
            >
              <div className={styles.dropdownButtonContent}>
                {fromCoinData && (
                  <img
                    src={getCoinImagePath(fromCoinData)}
                    alt={fromCoin}
                    className={styles.coinImage}
                    onError={(e) => {
                      if (fromCoin === 'USDT' && e.target.src.includes('USDT.webp')) {
                        e.target.src = '/assets/images/coins/TETHER.webp';
                      } else {
                        e.target.src = '/assets/images/coins/BTC.webp';
                      }
                    }}
                  />
                )}
                <span className={styles.coinName}>{fromCoin}</span>
              </div>
              <ChevronDown size={20} className={styles.chevron} />
            </button>
            {renderDropdown(isFromDropdownOpen, fromCoinData, handleFromCoinSelect, 'from')}
          </div>
        </div>
      </div>

      {/* Swap Button */}
      <div className={styles.swapButtonContainer}>
        <div className={styles.divider} />
        <button
          className={styles.swapButton}
          onClick={handleSwapCoins}
          aria-label="Swap currencies"
        >
          <ArrowUpDown size={20} />
        </button>
        <div className={styles.divider} />
      </div>

      {/* To Section */}
      <div className={styles.formGroup}>
        <label className={styles.label}>To</label>
        <div className={styles.inputGroup}>
          <input
            type="text"
            placeholder="0.00"
            value={toAmount}
            readOnly
            className={styles.amountInput}
          />
          <div className={`${styles.dropdownContainer} ${styles.dropdownContainer}`}>
            <button
              className={styles.dropdownButton}
              onClick={(e) => {
                e.stopPropagation();
                setIsToDropdownOpen(!isToDropdownOpen);
                setIsFromDropdownOpen(false);
              }}
            >
              <div className={styles.dropdownButtonContent}>
                {toCoinData && (
                  <img
                    src={getCoinImagePath(toCoinData)}
                    alt={toCoin}
                    className={styles.coinImage}
                    onError={(e) => {
                      if (toCoin === 'USDT' && e.target.src.includes('USDT.webp')) {
                        e.target.src = '/assets/images/coins/TETHER.webp';
                      } else {
                        e.target.src = '/assets/images/coins/BTC.webp';
                      }
                    }}
                  />
                )}
                <span className={styles.coinName}>{toCoin}</span>
              </div>
              <ChevronDown size={20} className={styles.chevron} />
            </button>
            {renderDropdown(isToDropdownOpen, toCoinData, handleToCoinSelect, 'to')}
          </div>
        </div>
      </div>

      {/* Summary */}
      {amount && parseFloat(amount) > 0 && (
        <div className={styles.summaryContainer}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Rate</span>
            <span className={styles.summaryValue}>
              1 {fromCoin} ≈ {baseRate} {toCoin}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Estimated Time</span>
            <span className={styles.summaryValue}>30 Seconds</span>
          </div>
          {fromCoin.toLowerCase() !== 'bjt' && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Fee</span>
              <span className={styles.summaryValue}>{getFeeText()}</span>
            </div>
          )}
          {parseFloat(toAmount) > 0 && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>You will receive</span>
              <span className={styles.summaryValueGreen}>
                {toAmount} {toCoin}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Action Button */}
      <Button
        variant="primary"
        fullWidth
        onClick={handleSwapNow}
        disabled={isLoading || !amount || parseFloat(amount) <= 0 || fromCoin === toCoin}
      >
        {isLoading ? 'Processing...' : 'Swap Now'}
      </Button>

      {/* Warning */}
      <div className={styles.warningContainer}>
        <Info size={20} className={styles.warningIcon} />
        <p className={styles.warningText}>
          Note: Fiat currency can only be converted into USDT.
        </p>
      </div>
    </div>
  );
};

export default SwapTab;
